import React, { useEffect, useRef, useState } from 'react';
import LoteChip, { getLoteChipStatus } from './LoteChip';
import LoteAsignacionDialog from './LoteAsignacionDialog';
import type { LoteAsignacion } from '../../services/loteService';
import {
  esControlSerie,
  productoControlaLote,
  type LoteMode,
} from '../../utils/loteLineaHelpers';

interface LoteLineaButtonProps {
  controlLoteSerie?: string | null;
  requiereVencimiento?: boolean | null;
  codigoArticulo: string;
  cantidad: number;
  nroUbicacion: number;
  mode: LoteMode;
  lotes?: LoteAsignacion[] | null;
  onChange: (lotes: LoteAsignacion[]) => void;
  disabled?: boolean;
  /** Si cambia y los lotes estan incompletos/vencidos, abre el dialogo automaticamente. */
  autoOpenSignal?: number | string | null;
  /** Chip compacto (labels cortos, sin forzar salto de linea). */
  dense?: boolean;
  /** Solo icono (p. ej. grilla POS). */
  iconOnly?: boolean;
  /** Si se informa, el diálogo solo ofrece estos lotes (p. ej. NC desde factura). */
  lotesPermitidos?: LoteAsignacion[] | null;
}

const LoteLineaButton: React.FC<LoteLineaButtonProps> = ({
  controlLoteSerie,
  requiereVencimiento,
  codigoArticulo,
  cantidad,
  nroUbicacion,
  mode,
  lotes,
  onChange,
  disabled,
  autoOpenSignal,
  dense,
  iconOnly,
  lotesPermitidos,
}) => {
  const [open, setOpen] = useState(false);
  const lastSignalRef = useRef<number | string | null | undefined>(undefined);
  const controla = productoControlaLote(controlLoteSerie);
  const qty = Number(cantidad) || 0;
  const status = getLoteChipStatus(controla, qty, lotes);

  useEffect(() => {
    if (!controla || disabled || autoOpenSignal == null) return;
    if (autoOpenSignal === lastSignalRef.current) return;
    if (status !== 'incomplete' && status !== 'expired') {
      lastSignalRef.current = autoOpenSignal;
      return;
    }
    if (qty <= 0 || !String(codigoArticulo ?? '').trim()) return;
    lastSignalRef.current = autoOpenSignal;
    setOpen(true);
  }, [autoOpenSignal, controla, disabled, status, qty, codigoArticulo]);

  if (!controla || !String(codigoArticulo ?? '').trim()) return null;

  return (
    <>
      <LoteChip
        controla={controla}
        cantidad={qty}
        lotes={lotes}
        dense={dense}
        iconOnly={iconOnly}
        readOnly={disabled}
        onClick={() => setOpen(true)}
      />
      <LoteAsignacionDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={(next) => {
          onChange(next);
          setOpen(false);
        }}
        codigoArticulo={codigoArticulo.trim()}
        cantidad={qty}
        nroUbicacion={nroUbicacion}
        mode={mode}
        esSerie={esControlSerie(controlLoteSerie)}
        requiereVencimiento={!!requiereVencimiento}
        initialLotes={lotes ?? undefined}
        readOnly={disabled}
        lotesPermitidos={lotesPermitidos}
      />
    </>
  );
};

export default LoteLineaButton;
